/**
 * RP4 — `<SceneContent>` extracted from `Scene3DStage.tsx`.
 *
 * Render orchestrator child of `<Canvas>`. Lives inside the R3F tree
 * so it can use `useThree` (for scene + gl) and `useFrame` (for
 * resolution uniform sync) — both of which require the R3F context.
 *
 * Composition shape:
 *   - `useSceneContentRefs` owns every per-instance ref + R3F
 *     passthroughs (camera, invalidate, spatial-index publish).
 *   - `installStageClickHandlers` wires pointerdown/pointerup picking.
 *   - `useSceneEffects` drives bulk-load + layer visibility +
 *     selection sync + hover bridge.
 *   - `<FrameLoops>` hosts the per-frame resolution sync + LOD
 *     viewport-cull passes.
 *
 * No behavior changes — pure code move from `Scene3DStage.tsx`.
 */

import * as React from "react";
import { useEffect, useMemo } from "react";
import { useThree } from "@react-three/fiber";
import * as THREE from "three";

import type { DimensionStyle } from "@dxf-ai-editor/drawing-model";

import type { Scene3DStageProps } from "../Scene3DStageProps";
import type { RenderableEntity, RendererHandle } from "../types";
import type { BatchedLayer } from "../batchedLayer";
import { type AdapterContext } from "../adapters";
import {
  buildAdapterCtxBase,
  type AdapterCtxLayerRow,
} from "./buildAdapterCtxBase";
import {
  useEditorStore,
  type EditorStore,
} from "../../state/editorStore";
import {
  type LayerVisibilityState,
} from "../state/layerVisibility";

// Phase 1.k (П35Р React #185 fix) — stable empty layer array sentinel.
// The `?? []` fallback inside the Zustand selector below allocated a
// fresh array on every store update, defeating `useSyncExternalStore`'s
// snapshot bail-out. With 685k entities + many subscribers it stacked
// past React's 50-update nested-render budget → error #185.
const EMPTY_LAYER_STATES: ReadonlyArray<LayerVisibilityState> = [];
import type { CameraStateSnapshot } from "../cameraApi";

import { FrameLoops } from "./FrameLoops";
import type { ViewportLiftRequests } from "../viewports/viewportLiftRequests";
import type { SpaceLayerCensusCache } from "./spaceLayerCensusCache";
import { installStageClickHandlers } from "./StagePointerHandlers";
import type { RefinedEntityPicker } from "./refinedEntityPick";
import { useSceneContentRefs } from "./useSceneContentRefs";
import { useSceneEffects } from "./useSceneEffects";
import { buildPreviewCallbacks } from "./SceneContentRefs";

export interface SceneContentProps {
  entities: Scene3DStageProps["entities"];
  /** PR-V0c — model-space entities (rendered inside layout viewports). */
  modelSpaceEntities: Scene3DStageProps["modelSpaceEntities"];
  /** PR-V0c — active space kind so the model-space layer knows when to render. */
  activeSpaceKind: string;
  /**
   * MC4 S2 P2 (iter-4 #1) — the active space id (`props.activeSpace?.id ??
   * "model"`). Threaded into `installStageClickHandlers` as the identity-mode
   * fallback space so an empty-layout / cache-direct paper click does NOT
   * default to "model" and pick invisible model geometry. See the
   * `activeSpaceId` doc on `InstallStageClickHandlersArgs`.
   */
  activeSpaceId: string;
  handleRef: React.MutableRefObject<RendererHandle | null>;
  batchedLayerRef: React.MutableRefObject<BatchedLayer | null>;
  onSelectEntity: Scene3DStageProps["onSelectEntity"];
  onSelectEntities: Scene3DStageProps["onSelectEntities"];
  selectedEntityIds: Scene3DStageProps["selectedEntityIds"];
  blockDefinitions: Scene3DStageProps["blockDefinitions"];
  cannoscaleName: Scene3DStageProps["cannoscaleName"];
  annotationScales: Scene3DStageProps["annotationScales"];
  /**
   * S10d — the document header's ANNOALLVISIBLE flag, threaded into the shared
   * `adapterCtxBase` so the typed annotative adapters hide/keep entities that
   * have no representation for the active CANNOSCALE.
   */
  annoAllVisible: Scene3DStageProps["annoAllVisible"];
  /**
   * The drawing's `$FILLMODE` (see `Scene3DStageProps`), threaded into the
   * shared `adapterCtxBase` so every renderable carries it.
   */
  fillmode: Scene3DStageProps["fillmode"];
  /** PR-LT3 — drawing LTYPE table for custom-linetype dash resolution. */
  linetypes: Scene3DStageProps["linetypes"];
  /** PR-LT3 variant A (A2) — GLOBAL dash-scale sysvars (LTSCALE / MS|PS). */
  linetypeSysvars: Scene3DStageProps["linetypeSysvars"];
  /**
   * @codex #334 (P2-B) — STYLE table (name + fontName) for text dispatch.
   * Forwarded to `useSceneEffects` so a generically-named STYLE
   * dispatches text on its actual font file.
   */
  textStyles: Scene3DStageProps["textStyles"];
  spatialIndexRefForHover: React.MutableRefObject<
    import("../types").SpatialIndex | null
  >;
  liveCameraRef: React.MutableRefObject<THREE.Camera | null>;
  /**
   * Locked-layer set kept in sync with the SceneContent layerVisibility
   * effect. Outer Scene3DStage reads it to drive the grips overlay.
   */
  sharedLockedLayersRef: React.MutableRefObject<Set<string>>;
  /**
   * Codex PR #784 P1 — outer-owned parent → render-only satellites map,
   * adopted by `useSceneContentRefs` as `multileaderBlockChildrenRef` so the
   * outer paper-space viewport orchestrator can project satellite layers
   * (`scene/satelliteLayerProjection.ts`). Optional for callers without a
   * viewport pass.
   */
  sharedRenderSatellitesRef?: React.MutableRefObject<
    Map<string, RenderableEntity[]>
  >;
  /**
   * Codex PR #784 round 3 P1 #2 — outer-owned REVISION of that map, bumped by
   * the bulk-load whenever the map is cleared / repopulated (sync and async
   * lift paths) so the orchestrator's layer census recomputes after the
   * async lift lands (`scene/spaceLayerCensusCache.ts`). Adopted by
   * `useSceneContentRefs` as `renderSatellitesVersionRef`.
   */
  sharedRenderSatellitesVersionRef?: React.MutableRefObject<number>;
  /**
   * Codex PR #784 round 3 P2 — outer-owned model / paper layer census the
   * orchestrator gates on (`scene/spaceLayerCensusCache.ts`); adopted by
   * `useSceneContentRefs` as `spaceLayerCensusRef` so the `FrameLoops` relift
   * path unions a relifted parent's satellite layers into it.
   */
  sharedSpaceLayerCensusRef?: React.MutableRefObject<SpaceLayerCensusCache>;
  /**
   * Codex PR #784 round-9 P1 — outer-owned set of ids the ACTIVE PAPER-SPACE
   * VIEWPORTS need materialised at their OWN model scale, published by the
   * stage's viewport orchestrator (`viewports/viewportLiftRequests.ts`) and
   * consumed by `FrameLoops`, which unions it into the newly-visible diff so
   * those ids take the same on-demand lift the main camera drives. Optional for
   * callers without a viewport pass (model space, unit tests).
   */
  sharedViewportLiftRequestsRef?: React.MutableRefObject<ViewportLiftRequests>;
  /**
   * Codex PR #784 round 4 F2 — outer-owned GLOBAL off / frozen layer set,
   * adopted by `useSceneContentRefs` as `hiddenLayersRef` (the ref the layer
   * -visibility effect republishes and the click pick reads), so the stage's
   * pointer-move hover hit-test filters INSERT child geometry identically.
   * Optional for callers without the stage binder.
   */
  sharedHiddenLayersRef?: React.MutableRefObject<Set<string>>;
  /**
   * Codex PR #784 round 7 P2 — outer-owned OUTLET for the refined entity
   * picker. `installStageClickHandlers` publishes a picker closing over this
   * component's cache / typed resolver / adapter context into it, and the outer
   * `Scene3DStage` hands the SAME ref to `installStageInteractions` so `ZOOM
   * Object` resolves its pick through the click path's pipeline (wrapper lift +
   * child-distance refinement) rather than the bbox-only `SpatialIndex.pick`.
   * Optional for callers without the stage binder.
   */
  sharedRefinedEntityPickRef?: React.MutableRefObject<RefinedEntityPicker | null>;
  /**
   * Camera zoom snapshot ref (populated by `<CameraApiBinder>` inside
   * Canvas every frame). Read at mesh-build time for sub-pixel gating.
   */
  cameraStateRef: React.MutableRefObject<CameraStateSnapshot>;
  /**
   * R3F invalidate ref, populated inside <Canvas> via useThree's
   * invalidate so external listeners can request a real frame repaint.
   */
  invalidateRef: React.MutableRefObject<(() => void) | null>;
  /**
   * MC4 S2 — live viewport-redirect context (same ref the outer
   * Scene3DStage hands to `installStageInteractions`). Threaded into
   * `installStageClickHandlers` so click-pick + drag-window select route
   * through the active VP's model space in MSPACE_VIA_VP. Null outside a
   * layout / when no VP context exists — the click handlers then run the
   * legacy paper/model path unchanged.
   */
  viewportRedirectRef: React.MutableRefObject<
    import("../stageInteractions").ViewportRedirectContext | null
  >;
  /**
   * cmd-pan — live interactive realtime-pan session flag. When `current` is true
   * a LMB drag PANS the camera (handled by `installStageInteractions`), so the
   * R3F selection click/drag-pick handlers must NOT also fire (no entity pick or
   * window-select during a pan, AutoCAD parity). Read at pointer-event time.
   */
  panSessionActiveRef: React.MutableRefObject<boolean>;
}

export function SceneContent(
  props: SceneContentProps,
): React.ReactElement | null {
  const {
    entities,
    modelSpaceEntities,
    activeSpaceKind,
    activeSpaceId,
    handleRef,
    batchedLayerRef,
    onSelectEntity,
    onSelectEntities,
    selectedEntityIds,
    blockDefinitions,
    cannoscaleName,
    annotationScales,
    annoAllVisible,
    fillmode,
    linetypes,
    linetypeSysvars,
    textStyles,
    spatialIndexRefForHover,
    liveCameraRef,
    sharedLockedLayersRef,
    sharedRenderSatellitesRef,
    sharedRenderSatellitesVersionRef,
    sharedSpaceLayerCensusRef,
    sharedHiddenLayersRef,
    sharedRefinedEntityPickRef,
    cameraStateRef,
    invalidateRef,
    viewportRedirectRef,
    panSessionActiveRef,
  } = props;
  const { scene, gl, invalidate, size: rawSize, camera } = useThree((state) => ({
    scene: state.scene,
    gl: state.gl,
    invalidate: state.invalidate,
    size: state.size,
    camera: state.camera,
  }));
  // PR-35R streaming-wire fix — stabilize the `size` object reference
  // when the rendered dimensions are unchanged. R3F mints a fresh
  // `state.size` POJO on every store update (even pure-readback ones
  // like a Zustand `entityMetadataIndexVersion` bump from
  // `rebuildEntityMetadataIndexFromImportAsync`), which would otherwise
  // invalidate `useSceneEffects`'s bulk-load effect dep array post-
  // import and re-run the cache-direct `buildVisibleRenderables` pass
  // synchronously (OOM on 100k+ entity drawings). Reading width/height
  // explicitly + memoising lets unrelated store updates skip the
  // ineffective re-bulk-load.
  const size = useMemo(
    () => ({ width: rawSize.width, height: rawSize.height }),
    [rawSize.width, rawSize.height],
  );

  // RP4 — composite ref-bundle hook owns all the local THREE refs +
  // runs the three R3F passthrough effects (camera / invalidate /
  // spatial index → outer Scene3DStage refs).
  const refs = useSceneContentRefs({
    camera,
    invalidate,
    liveCameraRef,
    invalidateRef,
    spatialIndexRefForHover,
    handleRef,
    entitiesIdentity: entities,
    ...(sharedRenderSatellitesRef ? { sharedRenderSatellitesRef } : {}),
    ...(sharedRenderSatellitesVersionRef
      ? { sharedRenderSatellitesVersionRef }
      : {}),
    ...(sharedSpaceLayerCensusRef ? { sharedSpaceLayerCensusRef } : {}),
    ...(sharedHiddenLayersRef ? { sharedHiddenLayersRef } : {}),
  });

  // S9b (Codex round-8 P2 — "Reapply highlight state after satellite relift") —
  // live snapshot of the current selection for the FrameLoops relift re-apply.
  // A pure camera relift never re-runs the standalone selection-sync effect, so
  // the per-frame path reads the current selection through this ref to
  // re-highlight a block-content MLEADER's freshly re-indexed satellite slots.
  //
  // fix/selection-highlight-not-applied — the SAME snapshot is now also read by
  // `runSceneBulkLoad`, which faces the identical problem one level up: a
  // bulk-load pass disposes every `SelectionStateTexture` and builds fresh
  // all-zero ones, and it is never triggered by a selection change, so the
  // standalone selection-sync effect does not re-run to put the byte back.
  const selectedEntityIdsRef = React.useRef<readonly string[]>(selectedEntityIds);
  selectedEntityIdsRef.current = selectedEntityIds;

  // PR-I — layer table subscription.
  const layerStates = useEditorStore(
    (store: EditorStore) =>
      (store.geometryDocument.styleTables?.layers ??
        EMPTY_LAYER_STATES) as ReadonlyArray<LayerVisibilityState>,
  );

  // Codex P1 #2 (PR #87) — subscribe to `entityMetadataIndexVersion` so
  // bulk-load fires after a streaming import completes. The streaming
  // path leaves `state.drawing.entities` empty (intentionally — see
  // EMPTY_ENTITIES_IN_SPACE sentinel in editorStore), so without an
  // explicit trigger the bulk-load effect's dep array never changes and
  // the scene stays blank until the user pans/zooms. The index version
  // bumps exactly once at the end of import via
  // `rebuildEntityMetadataIndexFromImportAsync`, so it is the right
  // signal: it fires once post-stream and never again until the next
  // import / reset / commit.
  const entityMetadataIndexVersion = useEditorStore(
    (store: EditorStore) => store.entityMetadataIndexVersion,
  );

  const geometryDocumentForPreview = useEditorStore(
    (store: EditorStore) => store.geometryDocument,
  );
  const previewCallbackRefs = useMemo(
    () => buildPreviewCallbacks(geometryDocumentForPreview),
    [geometryDocumentForPreview],
  );

  // Lane H / S8 — DIMSTYLE table, name-keyed, for the typed
  // `dimensionFromEntity` adapter's DIMVAR resolution (mirrors the legacy
  // bag producer's `dimStylesByName` in `state/dxfGeometry.ts`).
  const dimensionStylesByName = useMemo(() => {
    const map = new Map<string, DimensionStyle>();
    for (const s of geometryDocumentForPreview.styleTables?.dimensionStyles ??
      []) {
      map.set(s.name, s);
    }
    return map;
  }, [geometryDocumentForPreview]);

  // Lane H / S9 — active annotation scale factor (CANNOSCALE → numeric),
  // for the typed `multileaderFromEntity` adapter's annotative-context
  // picking. Mirrors `SceneBulkLoad.ts`'s `resolvedAnnoScale` computation
  // (kept in ONE place so the bulk-load + pointer-pick + cache-direct
  // callers — all sharing `adapterCtxBase` — agree).
  const resolvedAnnotationScale = useMemo<number | null>(() => {
    if (!cannoscaleName) return null;
    const found = annotationScales?.find((s) => s.name === cannoscaleName);
    return found?.scaleFactor ?? null;
  }, [cannoscaleName, annotationScales]);

  // PR-V2 / fix/bylayer-colour-never-resolves-on-canvas — adapter context
  // base. `buildAdapterCtxBase` is the extracted, unit-testable builder
  // (`./buildAdapterCtxBase.ts`) — see its doc comment for the layer-table
  // wiring (`layerColors` / `layerLinetypes`) and the annotation-scale /
  // viewport SCOPED LIMITATION detail previously inlined here.
  //
  // `layerStates` is the `styleTables.layers` table cast to the narrower
  // visibility shape; the runtime objects carry `color` + `linetype`
  // (full `LayerDefinition`), so we read them through a structural
  // widening (`AdapterCtxLayerRow`).
  const adapterCtxBase = useMemo<AdapterContext>(
    () =>
      buildAdapterCtxBase({
        layerStates: layerStates as ReadonlyArray<AdapterCtxLayerRow>,
        dimensionStylesByName,
        blockDefinitions,
        resolvedAnnotationScale,
        cannoscaleName,
        // The pass's space IDENTITY — `entityForegroundHex` tells the layout's
        // own entities from the model space its viewports show, so ACI 7
        // contrasts with the background each half is really drawn on.
        activeSpaceId,
        activeSpaceKind,
        annoAllVisible,
        fillmode,
      }),
    [
      layerStates,
      dimensionStylesByName,
      blockDefinitions,
      resolvedAnnotationScale,
      cannoscaleName,
      annoAllVisible,
      fillmode,
      activeSpaceId,
      activeSpaceKind,
    ],
  );

  // PR-C: pointer-down click handler. Helper installs the listeners
  // and returns a teardown function the effect hands back.
  useEffect(() => {
    const canvas = gl.domElement;
    if (!canvas) return;
    if (!handleRef.current) return;
    return installStageClickHandlers({
      canvas,
      camera,
      size,
      handleRef,
      entities,
      selectedEntityIds,
      onSelectEntity,
      onSelectEntities,
      lockedLayersRef: refs.lockedLayersRef,
      hiddenLayersRef: refs.hiddenLayersRef,
      visibleSetCacheRef: refs.visibleSetCacheRef,
      buildPreview: previewCallbackRefs.buildPreview,
      // DEFUSE S11 — typed-first pick lift (see typedFirstRenderable.ts).
      resolveEntity: previewCallbackRefs.resolveEntity,
      adapterCtxBase,
      // MC4 S2 P2 (iter-4 #1) — identity-mode fallback space for pick + drag.
      activeSpaceId,
      // MC4 S2 — route pick / drag-window select through the active VP's
      // model space when editing in-VP (MSPACE_VIA_VP). Stable ref identity:
      // the redirect CONTEXT is refreshed on `.current` each render by the
      // owner (Scene3DStage), so the handlers always read fresh state without
      // re-installing the listeners.
      viewportRedirectRef,
      // cmd-pan — suppress selection click/drag while a realtime-pan session is
      // active (the LMB drag pans the camera instead). Stable ref (identity
      // never changes) read at event time; listed in deps for parity with
      // viewportRedirectRef — its stable identity means no extra re-install.
      panSessionActiveRef,
      // Codex PR #784 round 7 P2 — publish the refined picker for the
      // out-of-canvas ZOOM Object pick (the host reads the same ref). Cleared
      // by the returned teardown, so a torn-down canvas never leaves a stale
      // picker bound to a dead spatial index.
      refinedEntityPickRef: sharedRefinedEntityPickRef,
    });
  }, [
    gl,
    camera,
    size,
    handleRef,
    entities,
    onSelectEntity,
    onSelectEntities,
    selectedEntityIds,
    previewCallbackRefs,
    adapterCtxBase,
    activeSpaceId,
    refs.lockedLayersRef,
    refs.hiddenLayersRef,
    refs.visibleSetCacheRef,
    viewportRedirectRef,
    panSessionActiveRef,
    sharedRefinedEntityPickRef,
  ]);

  // RP4 — composite effects hook drives bulk-load + layer visibility +
  // selection sync + hover bridge from a single import surface.
  useSceneEffects({
    scene,
    gl,
    invalidate,
    size,
    camera,
    entities,
    modelSpaceEntities,
    activeSpaceKind,
    // fix/cache-direct-paper-space — the bulk load builds its cache-direct
    // spatial records for THIS space (plus model behind a layout) and re-runs
    // when it changes. Already a prop for the pick / drag-window fallback.
    activeSpaceId,
    blockDefinitions,
    cannoscaleName,
    annotationScales,
    linetypes,
    linetypeSysvars,
    textStyles,
    selectedEntityIds,
    // fix/selection-highlight-not-applied — the bulk-load pass rebuilds every
    // `SelectionStateTexture` from scratch, so it needs the live selection to
    // re-stamp onto the new slots. Same ref `FrameLoops` reads below.
    selectedEntityIdsRef,
    layerStates,
    geometryDocument: geometryDocumentForPreview,
    buildPreview: previewCallbackRefs.buildPreview,
    // DEFUSE S11 — typed-first bulk-load lift (see typedFirstRenderable.ts).
    resolveEntity: previewCallbackRefs.resolveEntity,
    adapterCtxBase,
    handleRef,
    batchedLayerRef,
    sharedLockedLayersRef,
    cameraStateRef,
    // Codex P1 #2 (PR #87) — feed the bulk-load effect a trigger that
    // fires post-streaming. See `entityMetadataIndexVersion` comment
    // above.
    entityMetadataIndexVersion,
    ...refs,
  });

  return (
    <FrameLoops
      bucketsRef={refs.bucketsRef}
      entityRendersRef={refs.entityRendersRef}
      handleRef={handleRef}
      prevCameraSnapshotRef={refs.prevCameraSnapshotRef}
      lodPointsCloudRef={refs.lodPointsCloudRef}
      lodPointProxyCloudsRef={refs.lodPointProxyCloudsRef}
      lodGroupsRef={refs.lodGroupsRef}
      liftedEntityIdsRef={refs.liftedEntityIdsRef}
      multileaderBlockChildrenRef={refs.multileaderBlockChildrenRef}
      spaceLayerCensusRef={refs.spaceLayerCensusRef}
      selectedEntityIdsRef={selectedEntityIdsRef}
      entityResolverRef={refs.entityResolverRef}
      batchedLayerRef={batchedLayerRef}
      {...(props.sharedViewportLiftRequestsRef
        ? { viewportLiftRequestsRef: props.sharedViewportLiftRequestsRef }
        : {})}
      linetypeResolutionRef={refs.linetypeResolutionRef}
      layerLinetypeMapRef={refs.layerLinetypeMapRef}
      linetypeSysvarsRef={refs.linetypeSysvarsRef}
      hiddenLayersRef={refs.hiddenLayersRef}
      lockedLayersRef={refs.lockedLayersRef}
    />
  );
}
